/**
 * Derived from http://cgi.axel-findling.de/cgi-bin/romdat
 * Originally written by Axel Findling.
 * Modified by Sheean Spoel, Digital Humanities Lab, Utrecht University.
 */
import { Calendar } from './calendar';
import { isLeapYear, createDate } from './common';
import { InvalidDateException } from './invalid-date-exception';
import { HistoricalDate } from './historical-date';

export const RomanMonths = {
    "Ian.": 1,
    "Feb.": 2,
    "Mart.": 3,
    "Apr.": 4,
    "Mai.": 5,
    "Jun.": 6,
    "Jul.": 7,
    "Sext.": 8,
    "Sept.": 9,
    "Oct.": 10,
    "Nov.": 11,
    "Dec.": 12
};
const monthNames: RomanMonth[] = ["Ian.", "Feb.", "Mart.", "Apr.", "Mai.", "Jun.", "Jul.", "Sext.", "Sept.", "Oct.", "Nov.", "Dec."];
export const RomanDays = {
    "": 1,
    "pr.": 2,
    "postr.": 0,
    "a.d.III.": 3,
    "a.d.IV.": 4,
    "a.d.V.": 5,
    "a.d.VI.": 6,
    "a.d.VII.": 7,
    "a.d.VIII.": 8,
    "a.d.IX.": 9,
    "a.d.X.": 10,
    "a.d.XI.": 11,
    "a.d.XII.": 12,
    "a.d.XIII.": 13,
    "a.d.XIV.": 14,
    "a.d.XV.": 15,
    "a.d.XVI.": 16,
    "a.d.XVII.": 17,
    "a.d.XVIII.": 18,
    "a.d.XIX.": 19
};
export const RomanDayLong: { [day in RomanDay]?: string } = {
    'pr.': 'pridie',
    'postr.': 'postridie'
};
export const RomanTexts = { "Kal.": 1, "Non.": 2, "Id.": 3 };

const RomanMonthPatterns: [RegExp, RomanMonth][] = [
    [/^[ij]an(uari[ia]s?|)\.?/i, 'Ian.'],
    [/^feb(ruari[ia]s?|)\.?/i, 'Feb.'],
    [/^mar(ti[ia]s?|t|)\.?/i, 'Mart.'],
    [/^apr(lis?|)\.?/i, 'Apr.'],
    [/^ma[ij]\.?/i, 'Mai.'],
    [/^[ij]un(i[ia]s?|)\.?/i, 'Jun.'],
    [/^[ij]ul(i[ia]s?|)\.?/i, 'Jul.'],
    [/^(se[cxk](til(is|)|t)|augustini)\.?/i, 'Sext.'],
    [/^sep(tembris|temb|t|)\.?/i, 'Sept.'],
    [/^o[ck]t(obris|ob|)\.?/i, 'Oct.'],
    [/^nov(emb|)\.?/i, 'Nov.'],
    [/^de[ck]\.?/i, 'Dec.']];

const RomanDayPatterns: [RegExp, RomanDay][] = [
    [/^\.*/, ''],
    [/^pri?d?i?e?\.?/i, 'pr.'],
    [/^postr?i?d?i?e?\.?/i, 'postr.'],
    [/^a\.?d\.? ?iii\.?/i, 'a.d.III.'],
    [/^a\.?d\.? ?iv\.?/i, 'a.d.IV.'],
    [/^a\.?d\.? ?v\.?/i, 'a.d.V.'],
    [/^a\.?d\.? ?vi\.?/i, 'a.d.VI.'],
    [/^a\.?d\.? ?vii\.?/i, 'a.d.VII.'],
    [/^a\.?d\.? ?viii\.?/i, 'a.d.VIII.'],
    [/^a\.?d\.? ?ix\.?/i, 'a.d.IX.'],
    [/^a\.?d\.? ?x\.?/i, 'a.d.X.'],
    [/^a\.?d\.? ?xi\.?/i, 'a.d.XI.'],
    [/^a\.?d\.? ?xii\.?/i, 'a.d.XII.'],
    [/^a\.?d\.? ?xiii\.?/i, 'a.d.XIII.'],
    [/^a\.?d\.? ?xiv\.?/i, 'a.d.XIV.'],
    [/^a\.?d\.? ?xv\.?/i, 'a.d.XV.'],
    [/^a\.?d\.? ?xvi\.?/i, 'a.d.XVI.'],
    [/^a\.?d\.? ?xvii\.?/i, 'a.d.XVII.'],
    [/^a\.?d\.? ?xviii\.?/i, 'a.d.XVIII.'],
    [/^a\.?d\.? ?xix\.?/i, 'a.d.XIX.']
];

const RomanTextPatterns: [RegExp, RomanText][] = [
    [/^[ck]ale?n?d?[ai]?s?\.?/i, 'Kal.'],
    [/^non[ai]?s?\.?/i, 'Non.'],
    [/^e?idi?b?u?s?\.?/i, 'Id.']
];

export type RomanMonth = keyof typeof RomanMonths;
export type RomanDay = keyof typeof RomanDays;
export type RomanText = keyof typeof RomanTexts;

function matchBiggest<T>(text: string, patterns: [RegExp, T][]) {
    let candidateText = '';
    let candidate: T | null = null;

    for (let [pattern, key] of patterns) {
        let match = text.match(pattern);
        if (match && match[0].length >= candidateText.length) {
            candidateText = match[0];
            candidate = key;
        }
    }

    return { text: candidate, length: candidateText.length };
}

export class RomanDate {
    constructor(public day: RomanDay,
        public text: RomanText | '',
        public month: RomanMonth | '',
        public year: string,
        public calendar: Calendar = 'gregorian') {
    }

    static fromDate(date: HistoricalDate) {
        let romanDay: RomanDay | '' = '',
            romanText: RomanText | '' = '',
            romanMonthName: RomanMonth | '' = '';
        if (date.day !== undefined && date.month !== undefined) {
            const roman = romanCalendar(date);
            romanDay = roman.romanDay;
            romanText = roman.romanText;
            romanMonthName = roman.romanMonthName;
        } else if (date.month !== undefined) {
            romanMonthName = monthNames[date.month - 1];
        }

        let romanYear = date.year === undefined ? '' : toRomanNumber(date.year);

        return new RomanDate(romanDay, romanText, romanMonthName, romanYear, date.calendar);
    }

    static fromString(string: string, calendar: Calendar = 'gregorian') {
        let romanDay = matchBiggest(string, RomanDayPatterns);

        if (romanDay.text == null) {
            throw new InvalidDateException(`No day found in ${string}`);
        }

        let remainder = string.substring(romanDay.length).trimLeft();
        let romanText = matchBiggest(remainder, RomanTextPatterns);

        if (romanText.text == null) {
            throw new InvalidDateException(`No text part found in ${string} (${remainder}; ${romanDay.text})`);
        }

        remainder = remainder.substring(romanText.length).trimLeft();
        let romanMonth = matchBiggest(remainder, RomanMonthPatterns);

        if (romanMonth.text == null) {
            throw new InvalidDateException(`No month found in ${string} (${remainder})`);
        }

        let romanYear = remainder.substring(romanMonth.length).replace(/\s/g, '').toUpperCase();
        // normalize it and check validity
        romanYear = toRomanNumber(fromRomanNumber(romanYear));

        return new RomanDate(romanDay.text, romanText.text, romanMonth.text, romanYear, calendar);
    }

    toString() {
        return [
            ...(this.day ? [RomanDayLong[this.day] || this.day] : []),
            this.text === '' ? '??' : this.text,
            this.month === '' ? '??' : this.month,
            this.year === '' ? '??' : this.year].join(' ');
    }

    toDate() {
        let year = this.year.replace(/[^MDCLXVI]/gi, '').toUpperCase();
        let germanYear = fromRomanNumber(year);
        let romanDay = RomanDays[this.day];
        let offset = 0;
        if (this.day == 'postr.') {
            offset = 1;
            romanDay = 1;
        }

        let date = germanCalendar(
            romanDay,
            this.text || "Kal.",
            RomanMonths[this.month || "Ian."],
            germanYear,
            this.calendar);

        return createDate(
            this.year === '' ? undefined : germanYear,
            this.month === '' ? undefined : date.month,
            this.text === '' ? undefined : date.day,
            this.calendar).addDays(offset);
    }
}

export function fromRomanNumber(text: string) {
    // clear whitespace
    const value = `${text}`.replace(/\W/g, '').toUpperCase()
    // plain number?
    if (/^\d+$/.test(value)) {
        return parseInt(value, 10);
    }

    let index = 0;
    let result = 0;

    if (value.substr(index, 3) == "MMM") { result += 3000; index += 3; }
    else if (value.substr(index, 2) == "MM") { result += 2000; index += 2; }
    else if (value.substr(index, 1) == "M") { result += 1000; index += 1; }

    if (value.substr(index, 2) == "CM") { result += 900; index += 2; }
    else if (value.substr(index, 4) == "DCCC") { result += 800; index += 4; }
    else if (value.substr(index, 3) == "DCC") { result += 700; index += 3; }
    else if (value.substr(index, 2) == "DC") { result += 600; index += 2; }
    else if (value.substr(index, 1) == "D") { result += 500; index += 1; }
    else if (value.substr(index, 2) == "CD") { result += 400; index += 2; }
    else if (value.substr(index, 3) == "CCC") { result += 300; index += 3; }
    else if (value.substr(index, 2) == "CC") { result += 200; index += 2; }
    else if (value.substr(index, 1) == "C") { result += 100; index += 1; }

    if (value.substr(index, 2) == "XC") { result += 90; index += 2; }
    else if (value.substr(index, 4) == "LXXX") { result += 80; index += 4; }
    else if (value.substr(index, 3) == "LXX") { result += 70; index += 3; }
    else if (value.substr(index, 2) == "LX") { result += 60; index += 2; }
    else if (value.substr(index, 1) == "L") { result += 50; index += 1; }
    else if (value.substr(index, 2) == "XL") { result += 40; index += 2; }
    else if (value.substr(index, 3) == "XXX") { result += 30; index += 3; }
    else if (value.substr(index, 2) == "XX") { result += 20; index += 2; }
    else if (value.substr(index, 1) == "X") { result += 10; index += 1; }

    if (value.substr(index, 2) == "IX") { result += 9; index += 2; }
    else if (value.substr(index, 4) == "VIII") { result += 8; index += 4; }
    else if (value.substr(index, 3) == "VII") { result += 7; index += 3; }
    else if (value.substr(index, 2) == "VI") { result += 6; index += 2; }
    else if (value.substr(index, 1) == "V") { result += 5; index += 1; }
    else if (value.substr(index, 2) == "IV") { result += 4; index += 2; }
    else if (value.substr(index, 3) == "III") { result += 3; index += 3; }
    else if (value.substr(index, 2) == "II") { result += 2; index += 2; }
    else if (value.substr(index, 1) == "I") { result += 1; index += 1; }

    if (index != value.length) {
        throw new InvalidDateException(`${value} is not a valid Roman number`);
    }

    return result;
}

function germanCalendar(romanDay: number, romanText: RomanText, romanMonth: number, year: number, calendar: Calendar) {
    if (romanText == 'Kal.' && romanMonth == 3 && isLeapYear(year, calendar)) {
        if (romanDay == 2) {
            return { day: 29, month: 2 };
        } else if (romanDay > 1) {
            romanDay--;
        }
    }

    let firstDay = false;
    let daysBefore = romanDay;
    let day = 0;
    let month = romanMonth;

    if (daysBefore == 1) {
        firstDay = romanText == 'Kal.';
    }

    if (firstDay) {
        day = 1;
    } else {
        if (romanText != 'Kal.' && (romanMonth == 3 || romanMonth == 5 || romanMonth == 7 || romanMonth == 10)) {
            daysBefore = daysBefore - 2;
        }

        switch (romanText) {
            case 'Kal.':
                // before the Kalendes
                month = romanMonth == 1 ? 12 : romanMonth - 1;

                let kal: number;
                if (month == 4 || month == 6 || month == 9 || month == 11) {
                    kal = 32;
                }
                else if (month == 2) {
                    kal = 30;
                }
                else {
                    kal = 33;
                }
                day = kal - daysBefore;
                break;
            case 'Non.':
                day = 6 - daysBefore;
                break;
            case 'Id.':
                day = 14 - daysBefore;
                break;
        }
    }

    if (day < 1 || day > 31) {
        throw new InvalidDateException();
    } else {
        return {
            day,
            month
        };
    }
}

function romanCalendar(date: HistoricalDate) {
    let day = date.day || 1;
    let month = date.month || 1;

    if ((day >= 15) && (month == 2) && date.isLeapYear) {
        day--;
    }
    else if ((day == 14) && (month == 2) && date.isLeapYear) {
        return {
            romanDay: 'a.d.XVII.' as RomanDay,
            romanText: 'Kal.' as RomanText,
            romanMonth: 3,
            romanMonthName: 'Mart.' as RomanMonth
        };
    }

    let monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let text: 0 | 1 | 2 = 0;
    let beforeText = 0;
    let textDay = day;
    let romanMonth = month;

    if (textDay == 1) {
        textDay = monthDays[month - 1] + 1;
    }

    if ((romanMonth == 3) || (romanMonth == 5) || (romanMonth == 7) || (romanMonth == 10)) {
        // within the Id.?
        if (textDay < 16) {
            text = 1;
            textDay = textDay + 17;
            if (textDay < 25) {
                text = 2;
                textDay = textDay + 8;
            }
        }
    } else if (textDay < 14) {
        text = 1;
        monthDays[1] = monthDays[3] = monthDays[5] = monthDays[8] = monthDays[10] = 31;
        textDay = textDay + 19;
        if (textDay < 25) {
            text = 2;
            textDay = textDay + 8;
        }
    }

    // how many days before the Kal. Id. or Non.?
    beforeText = monthDays[month - 1] + 2 - textDay;

    let romanDay: RomanDay, romanText: RomanText, romanMonthName: RomanMonth;

    if (beforeText == 1) {
        romanDay = "";
        if (text == 0) {
            romanMonth--;
        }
    }
    else if (beforeText == 2) {
        romanDay = "pr.";
    }
    else {
        romanDay = `a.d.${toRomanNumber(beforeText)}.` as RomanDay;
    }

    switch (text) {
        case 0:
            romanText = "Kal.";
            romanMonth++;
            if (romanMonth == 13) {
                romanMonth = 1;
            }
            break;
        case 1:
            romanText = "Id.";
            break;
        case 2:
            romanText = "Non.";
            break;
        default:
            throw new InvalidDateException(`Unhandled text type ${text}`);
    }

    romanMonthName = monthNames[romanMonth - 1];

    return { romanDay, romanText, romanMonthName };
}

export function toRomanNumber(value: number) {
    let thousand = (value - value % 1000) / 1000;
    let hundred = ((value - value % 100) / 100) - (thousand * 10);
    let decimal = ((value - value % 10) / 10) - (thousand * 100 + hundred * 10);
    let digit = value - (thousand * 1000 + hundred * 100 + decimal * 10);
    return ["", "M", "MM", "MMM"][thousand] +
        ["", "C", "CC", "CCC", "CD", "D", "DC", "DCC", "DCCC", "CM"][hundred] +
        ["", "X", "XX", "XXX", "XL", "L", "LX", "LXX", "LXXX", "XC"][decimal] +
        ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"][digit];
}
